import {
  SkyDialog,
  SkyDropdown,
  SkyInput,
  SkyLink,
  SkyMessageBar,
  SkyRadioGroup,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useEffect, useId, useState, type ReactNode } from "react";
import { Link } from "wouter";
import type { DestinationType } from "@shared/types/instancesAdmin";
import type { TenantResourceTargetsView } from "@shared/types/tenantResources";
import {
  NEW_TENANT_NAME_PATTERN,
  TENANT_NAME_MAX,
  TENANT_NAME_TAKEN_MESSAGE,
  TENANT_START_INSTANCES,
  WEBSITE_MESSAGE,
  createTenantBodySchema,
  newTenantNameSchema,
  tenantNameFromDisplayName,
  websiteSchema,
  type TenantNameAvailability,
  type TenantStartInstance,
  type TenantStatus,
  type TenantView,
} from "@shared/types/tenants";
import { SingleCheckbox } from "@/components/ui/SingleCheckbox";
import { useTenantNameAvailability } from "@/hooks/use-tenants";
import { ApiClientError } from "@/lib/api";
import { ROUTES, absolutePath } from "@/lib/routes";
import { ErrorState } from "./ScreenState";
import { effectiveDefaultTargetId } from "./DefaultTargetPicker";
import { targetOptionText } from "./TenantResourcesDrawer";

/**
 * Create or edit a tenant (FR-20; round 5: one page and one dialog; round 6 A1 and the owner's
 * updates of 2026-10-01 23:10 and 23:25: two names, each with one job, and one status; round 6
 * T-04, 2026-10-02: "Rename. No need to use Customer. Keep it simple": the customer above each
 * tenant folded into it, platform 0027).
 *
 * A tenant has at least one instance ("a house needs at least a room"). The dialog has two name
 * fields: **Display name** (spaces allowed, editable; `tenants.display_name`) and **Name**
 * (`tenants.name`, round 5's "Short name": lowercase letters and digits, fixed once created, used
 * in vault, database and resource names), and one **Status**. A NEW tenant is one form in two
 * sections, saved by ONE `POST /platform/tenants`, all or nothing:
 *
 * - **Tenant**: Display name and Name (filled from the display name, up to `TENANT_NAME_MAX`,
 *   until the person edits it, and checked for availability as it is typed,
 *   `GET /platform/tenants/name-available`, debounced; a taken name says so and suggests
 *   nothing, owner 2026-10-01 08:50), then Website (required, https; kept for reference) and Key
 *   Vault, then the one Warehouse its instances load into (create) or its Status (edit).
 * - **Instances**: Development (ticked) and Production, at least one.
 *
 * The create fits a 1440x900 screen with no scrolling (r5 polish, post-release check of 5cce69a):
 * fields two to a row (`Row`), the availability sentence directly under the Name, and what the
 * create does said in the dialog's description rather than a banner at the bottom.
 *
 * An edit is one `PUT /platform/tenants/:id` (the page does that), and has no Instances section:
 * it shows the tenant's instance count, linking to Instances, where instances are managed.
 *
 * Two fields are fixed once the tenant exists, and the dialog SHOWS them read-only with the
 * reason rather than hiding them (a field that vanishes between create and edit reads as a bug;
 * one that is greyed and explains itself teaches the rule once):
 *
 * - **Name** (`tenants.name`). It names the tenant's database and vault and every operator
 *   runbook; renaming it would rename things that already exist elsewhere.
 * - **Key Vault name.** A pointer, never a secret (SEC-2). Moving a tenant to another vault is a
 *   migration, not an edit.
 *
 * The Microsoft Entra directory (tenant) ID is asked for only while group access is on (its one
 * reader); off, the field is hidden and an edit leaves the stored value as it is.
 *
 * Validation runs the SHARED zod schemas, so the message a field shows is the message the server
 * would have produced. The test ids keep their round 5 words (`customer-name`, `tenant-slug`,
 * `customer-dialog` and the rest): e2e specs read them, and no person sees them.
 */

/**
 * The tenant's Microsoft Entra directory (round 4 item 15). Its one reader is directory-group
 * access: a mapping matches a token only when the token's `tid` is this value
 * (`GroupAccessService.directoryOf`). It decides nothing about who may sign in.
 */
export const ENTRA_DIRECTORY_LABEL = "Microsoft Entra directory (tenant) ID";
/** What the Tenants grid says where no directory is set, instead of a dash. */
export const ENTRA_DIRECTORY_UNSET = "Not set: directory-group access is off";
const ENTRA_DIRECTORY_INVALID =
  "Enter the directory's tenant ID, a GUID such as 19393f3e-7639-4afe-bd82-ffb6e79b24b3.";

/**
 * The owner's words for the Name field's help text (2026-10-01 23:10; the field round 5 called
 * "Short name"). The cap comes from `TENANT_NAME_MAX`, its one home, so this text and
 * the server's message never disagree.
 */
export const NAME_HELP = `Used in database, vault and resource names. Lowercase letters and digits, up to ${TENANT_NAME_MAX}. Cannot change later.`;

/** The two name fields' labels (owner 2026-10-01 23:25). */
export const DISPLAY_NAME_LABEL = "Display name";
export const NAME_LABEL = "Name";

/**
 * What a create does, said once above the fields (r5 polish): it was a banner under the
 * Instances section, below the visible part of the dialog at 1440x900. One line at 1440x900, as
 * are the tenant hints below, so the create fits with the Entra field too (review r1).
 */
const CREATE_DESCRIPTION =
  "Created together: the tenant first, then each instance in it. Setup takes a few minutes.";

/** Under the Warehouse choice: one warehouse for every instance the create starts. */
const WAREHOUSE_NOTE = "Every instance below loads into it.";

/** Beside the instance boxes. Each instance is a schema in the tenant's database (the dialog says so). */
const INSTANCES_NOTE = "At least one. Add more later from Instances.";

const NO_INSTANCE_MESSAGE = "Pick at least one instance: Development or Production.";

/** How long the Name rests before it is checked: one request per pause, not per key. */
const NAME_CHECK_DEBOUNCE_MS = 300;
const WEBSITE_CHECK_DEBOUNCE_MS = 400;

const STATUS_OPTIONS = [
  { value: "active", text: "Active" },
  { value: "disabled", text: "Disabled" },
];

type DefenderChoice = "default" | "on" | "off";

const DEFENDER_VALUES: Record<DefenderChoice, boolean | null> = {
  default: null,
  on: true,
  off: false,
};

function defenderChoiceOf(stored: boolean | null | undefined): DefenderChoice {
  if (stored === true) return "on";
  return stored === false ? "off" : "default";
}

/** What a change of Defender for Storage does, said beside the select (design §7.2). */
export const DEFENDER_REAPPLY_SENTENCE =
  "Changing this re-applies the tenant's Azure resources. It may take a few minutes.";

/** What the target select is for, and the missing-choice refusal (design §6.4). */
export const PLACEMENT_TARGET_HINT =
  "Where the tenant's Azure resources are created. It cannot move later.";
export const PLACEMENT_TARGET_REQUIRED = "Choose a placement target.";

const WAREHOUSE_OPTIONS = [
  { value: "snowflake", label: "Snowflake" },
  { value: "databricks", label: "Databricks" },
];

export interface TenantFormValues {
  /** The Display name (owner 2026-10-01 23:25), `tenants.display_name`. */
  displayName: string;
  /** The Name (`tenants.name`): set once, at create. */
  name: string;
  /** Null when the field is empty: required on create, cleared on edit. */
  website: string | null;
  entraTenantId: string | null;
  /** The one Status. */
  status: TenantStatus;
  /** Empty means "this deployment's vault". */
  keyVaultName: string;
  /** Create only: the instances the tenant starts with, at least one. */
  instances: TenantStartInstance[];
  /** Create only: the warehouse every one of them loads into. */
  destinationType: DestinationType;
  /** Create in a per-tenant deployment only (ADR 0010): the registered target chosen. */
  resourcesTargetId?: string;
  /** Edit of a per-tenant tenant only: `true`, `false`, or `null` for the target's default. */
  defenderForStorage?: boolean | null;
}

export interface TenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Null creates; a row edits. */
  tenant: TenantView | null;
  onSubmit: (values: TenantFormValues) => void;
  isSaving: boolean;
  error: unknown;
  /** Group access is on (`GET /me` `features.groupAccess`): only then is the directory asked for. */
  showEntraDirectory: boolean;
  /**
   * The registered placement targets of a per-tenant deployment (ADR 0010, design §6.4); absent in
   * a shared one, where a create has no target to pick and keeps its Key Vault field.
   */
  placement?: TenantResourceTargetsView | null;
}

interface FieldErrors {
  displayName?: string;
  website?: string;
  entraTenantId?: string;
  tenantName?: string;
  keyVaultName?: string;
  target?: string;
}

/** The longest Key Vault name the API takes (`createTenantSchema`, B-23). */
export const KEY_VAULT_NAME_MAX = 127;

/** What the Display name is for (round 6 A1; owner 2026-10-01 23:25): the one shown name. */
export const DISPLAY_NAME_HINT = "Shown in the switcher, emails and the audit trail.";

/** The value once it has stopped changing for `delayMs` (the connector picker's pattern). */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return settled;
}

function Section({
  title,
  description,
  testId,
  children,
}: {
  title: string;
  description?: ReactNode;
  testId: string;
  children: ReactNode;
}) {
  const headingId = useId();
  return (
    <section
      aria-labelledby={headingId}
      className="flex flex-col gap-2 border-t border-border pt-4 first:border-t-0 first:pt-0"
      data-testid={testId}
    >
      <div className="flex flex-col gap-1">
        <SkyText as="h3" id={headingId} size={300} weight="semibold" block>
          {title}
        </SkyText>
        {description ? (
          <SkyText size={200} color="muted" block>
            {description}
          </SkyText>
        ) : null}
      </div>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
}

/**
 * Fields two to a row where the dialog is wide enough, one to a row where it is not (a phone).
 * One track rule and no breakpoint variant: skypoint-ui's stylesheet re-ships base utilities
 * after the app's, so an `sm:grid-cols-2` override could lose the cascade (`PageHeader.tsx`).
 * `auto-fill` keeps the tracks when a row has one field, so it stays in the left column.
 */
function Row({ testId, children }: { testId: string; children: ReactNode }) {
  return (
    <div
      className="grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] items-start gap-4"
      data-testid={testId}
    >
      {children}
    </div>
  );
}

/** A 409 about the Name (a Name in use, a database another tenant holds), not the Display name. */
function isTenantNameConflict(error: unknown): boolean {
  if (!(error instanceof ApiClientError) || error.status !== 409) return false;
  return error.message === TENANT_NAME_TAKEN_MESSAGE || error.message.startsWith('The name "');
}

function dialogCopy(tenant: TenantView | null): {
  title: string;
  description: string;
  action: string;
} {
  if (tenant) {
    return {
      title: `Edit ${tenant.displayName}`,
      description: "The Name and Key Vault are fixed. Everything else can change.",
      action: "Save changes",
    };
  }
  return { title: "New tenant", description: CREATE_DESCRIPTION, action: "Create tenant" };
}

function tenantNameInvalid(
  fieldError: string | undefined,
  conflict: boolean,
  answer: TenantNameAvailability | undefined,
): boolean {
  return Boolean(fieldError || conflict || (answer && !answer.available));
}

/** A taken name is said once, by the availability line or the 409 banner. */
function nameMessage(fieldError: string | undefined): string | undefined {
  return fieldError === TENANT_NAME_TAKEN_MESSAGE ? undefined : fieldError;
}

/** How many instances sit under the tenant, linked to them on Instances. */
function TenantInstancesNote({ tenant }: { tenant: TenantView }) {
  return (
    <>
      <SkyLink
        as={Link}
        href={absolutePath(`${ROUTES.adminInstances}?tenant=${tenant.id}`)}
        testId="customer-dialog-instances-link"
      >
        {`${tenant.instanceCount} ${tenant.instanceCount === 1 ? "instance" : "instances"}`}
      </SkyLink>{" "}
      {tenant.instanceCount === 1 ? "sits" : "sit"} under it, managed from Instances. Disabling the
      tenant closes them and keeps their history readable.
    </>
  );
}

/** Whether the typed Name is free: available, taken, not checkable now, or being checked. */
function NameAvailability({
  name,
  answer,
  checkFailed,
}: {
  name: string;
  answer: TenantNameAvailability | undefined;
  checkFailed: boolean;
}) {
  function renderAnswer(): ReactNode {
    if (answer?.available) {
      return (
        <SkyText size={200} color="success" block>
          {`${name} is available.`}
        </SkyText>
      );
    }
    if (answer) {
      return (
        <SkyText size={200} color="danger" block>
          {TENANT_NAME_TAKEN_MESSAGE}
        </SkyText>
      );
    }
    if (checkFailed) {
      return (
        <SkyText size={200} color="muted" block>
          The name could not be checked now. Saving checks it again.
        </SkyText>
      );
    }
    return (
      <SkyText size={200} color="muted" block>
        {`Checking ${name}…`}
      </SkyText>
    );
  }

  return (
    <div role="status" aria-live="polite" data-testid="tenant-slug-availability">
      {renderAnswer()}
    </div>
  );
}

/** The Status (edit) or the Warehouse every instance below loads into (create). */
function StatusOrWarehouse({
  isEdit,
  status,
  onStatusChange,
  destinationType,
  onDestinationTypeChange,
}: {
  isEdit: boolean;
  status: TenantStatus;
  onStatusChange: (status: TenantStatus) => void;
  destinationType: DestinationType;
  onDestinationTypeChange: (type: DestinationType) => void;
}) {
  if (isEdit) {
    return (
      <SkyDropdown
        label="Status"
        options={STATUS_OPTIONS}
        selectedOptions={[status]}
        onOptionSelect={(values) => onStatusChange((values[0] as TenantStatus) ?? "active")}
        testId="customer-status"
      />
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <SkyRadioGroup
        label="Warehouse"
        layout="horizontal"
        options={WAREHOUSE_OPTIONS}
        value={destinationType}
        onChange={(value) => onDestinationTypeChange(value as DestinationType)}
        testId="customer-warehouse"
      />
      <SkyText size={200} color="muted" block>
        {WAREHOUSE_NOTE}
      </SkyText>
    </div>
  );
}

/** The instances a new tenant starts with, and the rule that at least one is ticked. */
function InstancesSection({
  instances,
  noInstance,
  onToggle,
}: {
  instances: TenantStartInstance[];
  noInstance: boolean;
  onToggle: (key: TenantStartInstance, checked: boolean) => void;
}) {
  return (
    <Section title="Instances" testId="customer-instances-section">
      {/* One line: the boxes, then the rule beside them, which turns into the refusal when
          none is ticked, in the same place, so the dialog does not grow (r5 polish). */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        {TENANT_START_INSTANCES.map((entry) => (
          <SingleCheckbox
            key={entry.key}
            label={entry.name}
            checked={instances.includes(entry.key)}
            onChange={(checked) => onToggle(entry.key, checked)}
            testId={`customer-instance-${entry.key}`}
          />
        ))}
        {noInstance ? (
          <SkyText size={200} color="danger" testId="customer-instances-required">
            {NO_INSTANCE_MESSAGE}
          </SkyText>
        ) : (
          <SkyText size={200} color="muted">
            {INSTANCES_NOTE}
          </SkyText>
        )}
      </div>
    </Section>
  );
}

/**
 * The Name, then what is wrong with it or whether it is free, then its help: the reason a field is
 * red is the first line under it (r5 polish). The help is the input's description, as SkyInput's
 * own hint would be.
 */
function TenantNameField({
  isEdit,
  value,
  onChange,
  helpId,
  fieldError,
  conflict,
  answer,
  showAvailability,
  checkFailed,
}: {
  isEdit: boolean;
  value: string;
  onChange: (value: string) => void;
  helpId: string;
  fieldError: string | undefined;
  /** A 409 about the Name, until the Name changes: the banner speaks for it. */
  conflict: boolean;
  answer: TenantNameAvailability | undefined;
  /** A Name still to be created that the rule allows: its availability line shows. */
  showAvailability: boolean;
  checkFailed: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <SkyInput
        label={NAME_LABEL}
        required={!isEdit}
        readOnly={isEdit}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-describedby={helpId}
        validationState={tenantNameInvalid(fieldError, conflict, answer) ? "error" : "none"}
        // A taken name is said once, by the availability line or the 409 banner.
        validationMessage={nameMessage(fieldError)}
        testId="tenant-slug"
      />
      {/* After a 409 the banner speaks for the name; the earlier check's answer is stale. */}
      {showAvailability && !conflict && value.trim() !== "" ? (
        <NameAvailability name={value.trim()} answer={answer} checkFailed={checkFailed} />
      ) : null}
      <SkyText id={helpId} size={200} color="muted" block>
        {isEdit
          ? "Set at creation. It names the database and vault, so it cannot change."
          : NAME_HELP}
      </SkyText>
    </div>
  );
}

/** Website and Key Vault, two to a row. */
function WebsiteAndVault({
  isEdit,
  website,
  onWebsiteChange,
  onWebsiteBlur,
  websiteError,
  keyVaultName,
  onKeyVaultNameChange,
  keyVaultError,
  showVault,
}: {
  isEdit: boolean;
  /** False for a per-tenant tenant, whose vault is its own and is not typed (ADR 0010). */
  showVault: boolean;
  website: string;
  onWebsiteChange: (website: string) => void;
  onWebsiteBlur: () => void;
  websiteError: string | undefined;
  keyVaultName: string;
  onKeyVaultNameChange: (name: string) => void;
  keyVaultError: string | undefined;
}) {
  return (
    <Row testId="customer-dialog-row-tenant-names">
      <SkyInput
        label="Website"
        type="url"
        required={!isEdit}
        value={website}
        onChange={(event) => onWebsiteChange(event.target.value)}
        onBlur={onWebsiteBlur}
        placeholder="https://www.example.com"
        hint={
          isEdit
            ? "For reference. Leave it empty to remove it."
            : "For reference. Starts with https://."
        }
        validationState={websiteError ? "error" : "none"}
        validationMessage={
          websiteError && isEdit ? `${websiteError} Or leave it empty to remove it.` : websiteError
        }
        testId="customer-website"
      />
      {showVault ? (
        <SkyInput
          label="Key Vault name"
          readOnly={isEdit}
          value={keyVaultName}
          onChange={(event) => onKeyVaultNameChange(event.target.value)}
          hint={
            isEdit
              ? "Fixed once the tenant is created. Skypoint support can move it."
              : "Leave it empty to use this deployment's own vault."
          }
          maxLength={KEY_VAULT_NAME_MAX}
          validationState={keyVaultError ? "error" : "none"}
          validationMessage={keyVaultError}
          testId="tenant-key-vault"
        />
      ) : null}
    </Row>
  );
}

/** Create in a per-tenant deployment: the registered targets, the default preselected. */
function PlacementTargetField({
  placement,
  value,
  onChange,
  error,
}: {
  placement: TenantResourceTargetsView;
  value: string | null;
  onChange: (id: string | null) => void;
  error: string | undefined;
}) {
  return (
    <div className="flex flex-col gap-1" data-testid="tenant-placement-target">
      <SkyDropdown
        label="Placement target"
        required
        options={placement.targets.map((target) => ({
          value: target.id,
          text: targetOptionText(target),
        }))}
        selectedOptions={value ? [value] : []}
        onOptionSelect={(values) => onChange(values[0] ?? null)}
        validationState={error ? "error" : "none"}
        validationMessage={error}
        testId="tenant-placement-target-select"
      />
      <SkyText size={200} color="muted" block>
        {PLACEMENT_TARGET_HINT}
      </SkyText>
    </div>
  );
}

/** Edit of a per-tenant tenant: Default (with its effective On or Off), On, Off. */
function DefenderField({
  value,
  onChange,
  effective,
  storedIsDefault,
}: {
  value: DefenderChoice;
  onChange: (choice: DefenderChoice) => void;
  effective: boolean;
  storedIsDefault: boolean;
}) {
  // The effective value is the target's default only while nothing is stored.
  const defaultText = storedIsDefault
    ? `Default (${effective ? "On" : "Off"})`
    : "Default (the target's setting)";
  return (
    <div className="flex flex-col gap-1" data-testid="tenant-defender">
      <SkyDropdown
        label="Defender for Storage"
        options={[
          { value: "default", text: defaultText },
          { value: "on", text: "On" },
          { value: "off", text: "Off" },
        ]}
        selectedOptions={[value]}
        onOptionSelect={(values) => onChange((values[0] as DefenderChoice) ?? "default")}
        testId="tenant-defender-select"
      />
      <SkyText size={200} color="muted" block>
        {DEFENDER_REAPPLY_SENTENCE}
      </SkyText>
    </div>
  );
}

/**
 * The target a create starts on: the stored "Default target for new tenants", or, with none
 * stored, the only registered target, which is the one the server places a tenant in anyway
 * (`resolveDefaultTarget`, `shared/azure/tenantResourceTargets.ts`). With several targets and no
 * default the person picks one. (#744: a deployment with one target, a local run or CI included,
 * created no tenant from the dialog.)
 */
function preselectedTarget(placement: TenantDialogProps["placement"]): string | null {
  return placement ? effectiveDefaultTargetId(placement) : null;
}

/** ADR 0010: a create in a per-tenant deployment picks a target; only a shared vault is typed. */
function placementFields(
  isEdit: boolean,
  perTenantEdit: boolean,
  placement: TenantDialogProps["placement"],
): { showTargetSelect: boolean; showVault: boolean } {
  return {
    showTargetSelect: !isEdit && Boolean(placement),
    showVault: isEdit ? !perTenantEdit : !placement,
  };
}

/** The availability answer for the Name on screen, only once the check has settled on it. */
function settledAnswer<T>(
  checkable: boolean,
  settledTenantName: string,
  typedTenantName: string,
  data: T | undefined,
): T | undefined {
  return checkable && settledTenantName === typedTenantName ? data : undefined;
}

/** The form's own sentences for the Display name, never the schema's default text (#1408). */
const DISPLAY_NAME_REQUIRED = "Enter a display name.";
const DISPLAY_NAME_TOO_LONG = "A display name is at most 200 characters.";

function displayNameIssue(displayName: string): string | undefined {
  if (displayName.trim() === "") return DISPLAY_NAME_REQUIRED;
  if (!createTenantBodySchema.shape.displayName.safeParse(displayName).success) {
    return DISPLAY_NAME_TOO_LONG;
  }
  return undefined;
}

/** What Save says about each field before anything is sent; empty when the form may be sent. */
function tenantFormErrors({
  displayName,
  typedWebsite,
  typedDirectory,
  isEdit,
  showEntraDirectory,
  tenantNameError,
  keyVaultName,
  showTargetSelect,
  targetId,
}: {
  displayName: string;
  typedWebsite: string;
  typedDirectory: string;
  isEdit: boolean;
  showEntraDirectory: boolean;
  tenantNameError: string | undefined;
  keyVaultName: string;
  showTargetSelect: boolean;
  targetId: string | null;
}): FieldErrors {
  const next: FieldErrors = {};
  const displayNameError = displayNameIssue(displayName);
  if (displayNameError) next.displayName = displayNameError;
  if (typedWebsite === "") {
    if (!isEdit) next.website = WEBSITE_MESSAGE;
  } else if (!websiteSchema.safeParse(typedWebsite).success) {
    next.website = WEBSITE_MESSAGE;
  }
  if (
    showEntraDirectory &&
    typedDirectory !== "" &&
    !createTenantBodySchema.shape.entraTenantId.safeParse(typedDirectory).success
  ) {
    next.entraTenantId = ENTRA_DIRECTORY_INVALID;
  }
  if (tenantNameError) next.tenantName = tenantNameError;
  if (!isEdit && keyVaultName.trim().length > KEY_VAULT_NAME_MAX) {
    next.keyVaultName = `A Key Vault name is at most ${KEY_VAULT_NAME_MAX} characters.`;
  }
  if (showTargetSelect && targetId === null) next.target = PLACEMENT_TARGET_REQUIRED;
  return next;
}

/** The Microsoft Entra directory, asked for only while group access is on. */
function EntraDirectoryField({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (value: string) => void;
  error: string | undefined;
}) {
  return (
    <SkyInput
      label={ENTRA_DIRECTORY_LABEL}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      hint="Needed for directory-group access. Find it in the Microsoft Entra admin center, on Overview, as Tenant ID."
      placeholder="00000000-0000-0000-0000-000000000000"
      validationState={error ? "error" : "none"}
      validationMessage={error}
      testId="customer-entra-tenant-id"
    />
  );
}

export function TenantDialog({
  open,
  onOpenChange,
  tenant,
  onSubmit,
  isSaving,
  error,
  showEntraDirectory,
  placement,
}: TenantDialogProps) {
  const isEdit = tenant !== null;
  // ADR 0010: a per-tenant tenant (and a create in a per-tenant deployment) owns its vault, so no
  // Key Vault name is typed; the Defender select is for an existing per-tenant tenant only.
  const perTenantEdit = tenant?.resources?.mode === "per_tenant";
  const { showTargetSelect, showVault } = placementFields(isEdit, perTenantEdit, placement);
  // Seeded from the row, not synchronised to it: the caller remounts this component whenever
  // the dialog opens (see the `key` on the page), which is React's own answer to "reset state
  // when a prop changes" and leaves no effect to forget a field in.
  const [displayName, setDisplayName] = useState(tenant?.displayName ?? "");
  const [website, setWebsite] = useState(tenant?.website ?? "");
  const [entraTenantId, setEntraTenantId] = useState(tenant?.entraTenantId ?? "");
  const [status, setStatus] = useState<TenantStatus>(tenant?.status ?? "active");
  const [tenantName, setTenantName] = useState(tenant?.name ?? "");
  // The Name follows the Display name until the person types in it (create only).
  const [tenantNameEdited, setTenantNameEdited] = useState(isEdit);
  const [keyVaultName, setKeyVaultName] = useState(tenant?.keyVaultName ?? "");
  const [instances, setInstances] = useState<TenantStartInstance[]>(["development"]);
  const [destinationType, setDestinationType] = useState<DestinationType>("snowflake");
  const [targetId, setTargetId] = useState<string | null>(preselectedTarget(placement));
  const [defender, setDefender] = useState<DefenderChoice>(
    defenderChoiceOf(tenant?.resources?.defenderForStorage),
  );
  // #1408: after a Create press, or while the Website sentence shows, every field's sentence is
  // read live from the form, so all of them show at once and clear as each is fixed.
  const [attempted, setAttempted] = useState(false);
  const tenantNameHelpId = useId();

  // Availability: only for a tenant still to be created, and only for a name the rule allows.
  const tenantNameOpen = !isEdit;
  const typedTenantName = tenantName.trim();
  const settledTenantName = useDebouncedValue(typedTenantName, NAME_CHECK_DEBOUNCE_MS);
  const checkable = tenantNameOpen && NEW_TENANT_NAME_PATTERN.test(settledTenantName);
  const availability = useTenantNameAvailability(settledTenantName, checkable);
  // The query is keyed by the settled name, so its data is that name's answer.
  const answer = settledAnswer(checkable, settledTenantName, typedTenantName, availability.data);
  // A 409 on save speaks for the Name that was sent; changing the field clears it.
  const [tenantNameChangedSinceSave, setTenantNameChangedSinceSave] = useState(false);

  // #991: the Website is checked as the person types (a short pause, or at once on blur), with
  // the shared rule, so a bad value is said before Save. Save is off exactly while the sentence
  // shows, so it is never off without a reason. A click on a value not yet settled is caught by submit().
  const typedWebsiteNow = website.trim();
  const websiteInvalid =
    typedWebsiteNow !== "" && !websiteSchema.safeParse(typedWebsiteNow).success;
  const settledWebsite = useDebouncedValue(typedWebsiteNow, WEBSITE_CHECK_DEBOUNCE_MS);
  const [websiteBlurred, setWebsiteBlurred] = useState(false);
  const websiteLiveError =
    websiteInvalid && (websiteBlurred || settledWebsite === typedWebsiteNow)
      ? WEBSITE_MESSAGE
      : undefined;

  const noInstance = !isEdit && instances.length === 0;
  // Latched: once a sentence has shown, every field's sentence stays up until it is fixed, even
  // while a Website retype is waiting for its pause.
  if (websiteLiveError !== undefined && !attempted) setAttempted(true);
  const showAllErrors = attempted || websiteLiveError !== undefined;
  function currentFormErrors(): FieldErrors {
    return tenantFormErrors({
      displayName,
      typedWebsite: typedWebsiteNow,
      typedDirectory: entraTenantId.trim(),
      isEdit,
      showEntraDirectory,
      tenantNameError: tenantNameIssue(),
      keyVaultName,
      showTargetSelect,
      targetId,
    });
  }
  const fieldErrors: FieldErrors = showAllErrors ? currentFormErrors() : {};
  // A typed Website waits for its pause or blur (#991) even after a Create press: only the empty
  // case is read at once. submit() keeps its immediate check of the current value.
  if (typedWebsiteNow !== "") delete fieldErrors.website;
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;

  function changeDisplayName(value: string) {
    setDisplayName(value);
    if (!tenantNameEdited && tenantNameOpen) {
      setTenantNameValue(tenantNameFromDisplayName(value));
    }
  }

  function changeTenantName(value: string) {
    setTenantNameEdited(true);
    setTenantNameValue(value);
  }

  /** A new Name clears what was said about the old one, at once (owner 2026-10-01). */
  function setTenantNameValue(value: string) {
    if (value === tenantName) return;
    setTenantName(value);
    setTenantNameChangedSinceSave(true);
  }

  function toggleInstance(key: TenantStartInstance, checked: boolean) {
    setInstances((current) =>
      checked
        ? [...current.filter((value) => value !== key), key]
        : current.filter((v) => v !== key),
    );
  }

  function tenantNameIssue(): string | undefined {
    if (isEdit) return undefined;
    if (typedTenantName === "")
      return "Give the tenant a Name. It names its database and its vault.";
    const parsedTenantName = newTenantNameSchema.safeParse(typedTenantName);
    if (!parsedTenantName.success) {
      return parsedTenantName.error.issues[0]?.message ?? "That name is not valid.";
    }
    if (answer && !answer.available) return TENANT_NAME_TAKEN_MESSAGE;
    return undefined;
  }

  function submit() {
    if (noInstance) return;
    const typedWebsite = website.trim();
    const typedDirectory = entraTenantId.trim();
    setAttempted(true);
    if (Object.keys(currentFormErrors()).length > 0) return;
    setTenantNameChangedSinceSave(false);
    const directoryOrNull = typedDirectory === "" ? null : typedDirectory;
    onSubmit({
      displayName: displayName.trim(),
      name: typedTenantName,
      website: typedWebsite === "" ? null : typedWebsite,
      // Hidden while group access is off: the stored value is kept as it is.
      entraTenantId: showEntraDirectory ? directoryOrNull : (tenant?.entraTenantId ?? null),
      status,
      keyVaultName: keyVaultName.trim(),
      instances: TENANT_START_INSTANCES.map((entry) => entry.key).filter((key) =>
        instances.includes(key),
      ),
      destinationType,
      ...(showTargetSelect && targetId !== null ? { resourcesTargetId: targetId } : {}),
      ...(perTenantEdit ? { defenderForStorage: DEFENDER_VALUES[defender] } : {}),
    });
  }

  // A 409 on this dialog is the Name's (a Name in use, a database another tenant holds) or the
  // Display name's; the server's sentence says which. One about the Name is said by the
  // Name field, in the owner's words, until the Name changes.
  const conflictIsTenantName = isTenantNameConflict(error);
  const tenantNameConflict = conflictIsTenantName && !tenantNameChangedSinceSave;

  const copy = dialogCopy(tenant);

  // Asked for only while group access is on, its one reader.
  const entraField = showEntraDirectory ? (
    <EntraDirectoryField
      value={entraTenantId}
      onChange={setEntraTenantId}
      error={fieldErrors.entraTenantId}
    />
  ) : null;

  return (
    <SkyDialog
      open={open}
      onOpenChange={onOpenChange}
      title={copy.title}
      description={copy.description}
      // Large, with fields two to a row (r5 polish): at 1440x900 the whole create form, the
      // Name's availability sentence included, is in view with no scrolling. Narrower
      // screens get one field to a row (`Row`), and the body scrolls under a fixed footer.
      size="large"
      testId="customer-dialog"
      // SkyDialog closes itself on a primary action, which would throw away a form the server has
      // just refused. The dialog closes when the save succeeds, and only then.
      onBeforeClose={(reason) => reason !== "primaryAction"}
      primaryAction={{
        label: copy.action,
        onClick: submit,
        loading: isSaving,
        disabled: noInstance || websiteLiveError !== undefined || (attempted && hasFieldErrors),
      }}
      secondaryAction={{ label: "Cancel", onClick: () => onOpenChange(false) }}
    >
      <div className="flex flex-col gap-4">
        {error && !conflictIsTenantName ? (
          <ErrorState title="That change was not saved" error={error} />
        ) : null}

        {/* One Tenant section (round 6 A1; owner 2026-10-01 23:25): the two names, then where it
            lives. The test id keeps its round 5 name. */}
        <Section
          title="Tenant"
          testId="customer-tenant-section"
          description={tenant ? <TenantInstancesNote tenant={tenant} /> : undefined}
        >
          {tenantNameConflict ? (
            <SkyMessageBar
              intent="error"
              testId="tenant-slug-conflict"
              message={TENANT_NAME_TAKEN_MESSAGE}
            />
          ) : null}

          {/* Owner 2026-10-01 23:25: Display name and Name, then Website and Key Vault, then the
              Warehouse (create) or the Status (edit). */}
          <Row testId="customer-dialog-row-customer">
            <SkyInput
              label={DISPLAY_NAME_LABEL}
              required
              value={displayName}
              onChange={(event) => changeDisplayName(event.target.value)}
              hint={DISPLAY_NAME_HINT}
              validationState={fieldErrors.displayName ? "error" : "none"}
              validationMessage={fieldErrors.displayName}
              testId="customer-name"
            />

            <TenantNameField
              isEdit={isEdit}
              value={tenantName}
              onChange={changeTenantName}
              helpId={tenantNameHelpId}
              fieldError={fieldErrors.tenantName}
              conflict={tenantNameConflict}
              answer={answer}
              showAvailability={tenantNameOpen && NEW_TENANT_NAME_PATTERN.test(typedTenantName)}
              checkFailed={availability.isError && settledTenantName === typedTenantName}
            />
          </Row>

          <WebsiteAndVault
            isEdit={isEdit}
            website={website}
            onWebsiteChange={(value) => {
              setWebsite(value);
              setWebsiteBlurred(false);
            }}
            onWebsiteBlur={() => setWebsiteBlurred(true)}
            websiteError={fieldErrors.website ?? websiteLiveError}
            keyVaultName={keyVaultName}
            onKeyVaultNameChange={(name) => {
              setKeyVaultName(name);
            }}
            keyVaultError={fieldErrors.keyVaultName}
            showVault={showVault}
          />

          <Row testId="customer-dialog-row-tenant-places">
            {/* Create only: where every instance below loads its data, chosen once for all. */}
            <StatusOrWarehouse
              isEdit={isEdit}
              status={status}
              onStatusChange={setStatus}
              destinationType={destinationType}
              onDestinationTypeChange={setDestinationType}
            />
            {entraField}
          </Row>

          {showTargetSelect && placement ? (
            <PlacementTargetField
              placement={placement}
              value={targetId}
              onChange={(next) => {
                setTargetId(next);
              }}
              error={fieldErrors.target}
            />
          ) : null}
          {perTenantEdit && tenant?.resources ? (
            <DefenderField
              value={defender}
              onChange={setDefender}
              effective={tenant.resources.defenderForStorageEffective}
              storedIsDefault={tenant.resources.defenderForStorage === null}
            />
          ) : null}
        </Section>

        {isEdit ? null : (
          <InstancesSection
            instances={instances}
            noInstance={noInstance}
            onToggle={toggleInstance}
          />
        )}
      </div>
    </SkyDialog>
  );
}
